import { Autocomplete, TextField, ThemeProvider } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/pt-br'; // load on demand
import { useEffect, useMemo, useState } from 'react';
import { makeLevelLabel } from '../../components/CalendarEvent';
import { PageWrapper } from '../../components/PageWrapper';
import { DataAutocomplete } from '../../components/SelectResponsibles';
import { CategoryTable } from '../../components/Table';
import { StatusLabel } from '../../components/StatusLabel';
import { TeacherReport } from '../../components/TeacherReport';
import { useAppSelector } from '../../hooks';
import { apiService } from '../../services/api';
import {
  ClassEvent,
  ReportEntity,
  ReportStatus,
  StudentPerformance,
  UserRole,
} from '../../services/api.types';
import { dateTheme } from '../../theme';
import { getEndHour } from '../../utils/date';
import {
  translateClassRoleShortcode,
  translateReportStatus,
} from '../../utils/translate-enums';
import { Button } from '../../components/Button';
import { ListAlt } from '@mui/icons-material';
import { ReportsSummary } from '../../components/ReportsSummary';
import { Modal } from '../../components/Modal';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { usersService } from '../../services/users.service';
import { GenerateExcelButton } from '../../components/GenerateExcelButton';
import SummarizeIcon from '@mui/icons-material/Summarize';
import { saveAs } from 'file-saver';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import { FormProvider, useForm } from 'react-hook-form';
import { DateRangePicker } from '../../components/DateRangePicker';
import { Checkbox } from '../../components/Checkbox';

dayjs.locale('pt-br');

type Reports = {
  id: number;
  idEvent: number;
  Estudante?: string;
  Professor?: string;
  Data: string;
  Horário: string;
  Materia: string;
  Status: JSX.Element;
};

export type ReportInfo = {
  id: number;
  email: string;
  fullName: string;
  date: Date;
  startTimeClass: Date;
  endTimeClass: Date;
  studentName: string;
  responsibleFullName: string;
  classLocation: string;
  taughtContent: string;
  studentPerformance: StudentPerformance;
  generalObservation: string;
  status: ReportStatus;
  valueFromResponsible: number;
  valueToTeacher: number;
  valueToTrip: number;
  wasStudentAbsent: boolean;
  reviewRequest: string;
};

export const ReportsPage = () => {
  const [filterMonth, setFilterMonth] = useState<Dayjs>(dayjs());
  const [events, setEvents] = useState<ClassEvent[]>([]);
  const [openSummary, setOpenSummary] = useState<boolean>(false);
  const [showGenerateReportExcelDialog, setShowGenerateReportExcelDialog] =
    useState<boolean>(false);
  const [viewReport, setViewReport] = useState<boolean>(false);
  const [isFetchingUser, setIsFetchingUser] = useState<boolean>(false);
  const [isFetchingTable, setIsFetchingTable] = useState<boolean>(false);
  const [users, setUsers] = useState<DataAutocomplete[]>([]);
  const [selectedUser, setSelectedUser] = useState<DataAutocomplete | null>(
    null
  );
  const [reportFromUrl, setReportFromUrl] = useState<ReportInfo>();
  const [selectedReport, setSelectedReport] = useState<Reports | null>(null);
  const [reportsCategory, setReportsCategory] =
    useState<string>('Filtrar por...');
  const [searchParams] = useSearchParams();

  const user = useAppSelector((state) => state.user);

  const isTeacher = user?.role === UserRole.TEACHER;
  const isAdminOrEmployee =
    user?.role === UserRole.ADMIN || user?.role === UserRole.EMPLOYEE;
  const showReportsSummary = (selectedUser && isAdminOrEmployee) || isTeacher;
  const showSummaryExcelButton = selectedUser && isAdminOrEmployee;

  const handleSummaryOpen = () => setOpenSummary(true);
  const handleSummaryClose = () => setOpenSummary(false);

  const reportExcelForm = useForm({
    defaultValues: {
      startDate: dayjs().subtract(1, 'month').startOf('month'),
      endDate: dayjs().subtract(1, 'month').endOf('month'),
      includeSimulatedReports: true,
    },
  });

  useEffect(() => {
    if (isTeacher) return;

    setIsFetchingUser(true);
    usersService
      .getAllUsers({
        role: [UserRole.RESPONSIBLE, UserRole.STUDENT, UserRole.TEACHER],
      })
      .then((res) => {
        setUsers(
          res.users
            .filter(
              (u) =>
                u.role === UserRole.STUDENT ||
                u.role === UserRole.TEACHER ||
                (u.role === UserRole.RESPONSIBLE && u.responsible?.isStudent)
            )
            .map((u) => ({
              id: u.id,
              displayName: `${translateClassRoleShortcode(u.role)} - ${
                u.fullName
              }`,
              role: u.role,
            }))
        );
      })
      .finally(() => {
        setIsFetchingUser(false);
      });
  }, [isTeacher]);

  useEffect(() => {
    if (!filterMonth || isNaN(Number(filterMonth?.get('date')))) return;

    const startDate = filterMonth.startOf('month').toISOString();
    const endDate = filterMonth.endOf('month').toISOString();

    setIsFetchingTable(true);
    const isStudent =
      selectedUser?.role === UserRole.STUDENT ||
      selectedUser?.role === UserRole.RESPONSIBLE;
    if (isStudent) {
      apiService
        .getAllEventsWithReportsStudent(selectedUser.id, startDate, endDate)
        .then((res) => setEvents(res));
    } else if (selectedUser?.role === UserRole.TEACHER) {
      apiService
        .getAllEventsWithReportsTeacher(selectedUser.id, startDate, endDate)
        .then((res) => setEvents(res));
    } else if (user?.role === UserRole.TEACHER) {
      apiService
        .getAllEventsWithReportsTeacher(Number(user.id), startDate, endDate)
        .then((res) => setEvents(res));
    } else {
      setEvents([]);
    }
  }, [selectedUser, user, filterMonth]);

  const getCategory = (category: string): string => {
    if (category === 'Feito') {
      return 'DONE';
    } else if (category === 'Pendente') {
      return 'PENDING';
    } else {
      return 'ALL';
    }
  };

  const newCategory = (category: string): void => {
    setReportsCategory(getCategory(category));
  };

  const getReportSummary = (report: Reports): string => {
    const teacher = report.Professor ?? '';
    const student = report.Estudante ?? '';

    let summary =
      selectedUser?.role === UserRole.STUDENT
        ? `Professor: ${teacher}`
        : `Estudante: ${student}`;

    const date = report.Data;
    const hour = report.Horário.replace(' - ', ' às ');
    summary += ` - ${hour} - ${date}`;

    if (reportsCategory === 'ALL') {
      const status = report.Status.props.status;
      summary += ` - ${status}`;
    }

    return summary;
  };

  const rows: Reports[] = useMemo(() => {
    const reports = events
      .map((event) => {
        const reports = event.reports
          ?.map((r) => r)
          .filter(() => user?.role !== UserRole.STUDENT);

        return { ...event, reports };
      })
      .map((event) => {
        const associatedUserName =
          selectedUser?.role !== UserRole.STUDENT
            ? { Estudante: event.student.fullName }
            : { Professor: event.teacher.fullName };

        if (!event.reports) return [];

        return event.reports.map((report) => {
          const endHour = getEndHour(report.startHour, report.duration);

          return {
            id: report.id,
            idEvent: event.id,
            ...associatedUserName,
            Data: dayjs(report.date).format('DD/MM/YYYY'),
            Materia: makeLevelLabel[event.subject.level](event.subject.name),
            Horário: `${dayjs(report.startHour).format('LT')} - ${dayjs(
              endHour
            ).format('LT')}`,
            Status: (
              <StatusLabel status={translateReportStatus(report.status)} />
            ),
          };
        });
      })
      .flat();

    setIsFetchingTable(false);
    return reports.sort(
      (a, b) =>
        dayjs(a.Data, 'DD/MM/YYYY').valueOf() -
        dayjs(b.Data, 'DD/MM/YYYY').valueOf()
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, user?.role]);

  const reportInfo: ReportInfo | undefined = useMemo(() => {
    const event = events.find((event) => event.id === selectedReport?.idEvent);
    const report = event?.reports?.find(
      (report) => report.id === selectedReport?.id
    );

    if (!report || !event) return;

    const endHour = getEndHour(report.startHour, report.duration);
    return {
      id: report.id,
      email: event.teacher.email,
      fullName: event.teacher.fullName,
      date: report.date,
      startTimeClass: report.startHour,
      endTimeClass: endHour,
      studentName: event.student.fullName,
      responsibleFullName: event.responsible?.fullName,
      classLocation: report.place,
      taughtContent: report.taughtContent,
      studentPerformance: report.studentPerformance,
      generalObservation: report.observations,
      reviewRequest: report.reviewRequest,
      status: report.status,
      valueFromResponsible: report.valueFromResponsible,
      valueToTeacher: report.valueToTeacher,
      valueToTrip: report.valueToTrip,
      wasStudentAbsent: report.wasStudentAbsent,
    };
  }, [events, selectedReport?.id, selectedReport?.idEvent]);

  const updateReport = (res: ReportEntity) => {
    setEvents(
      events.map((event) => {
        event.reports?.forEach((report) => {
          if (report.id === res.id) Object.assign(report, res);
        });
        return event;
      })
    );
  };

  const handleSelect = (report: Reports) => {
    setSelectedReport(report);
    setViewReport(true);
  };

  useEffect(() => {
    const reportId = searchParams.get('reportId');
    if (!reportId) return;
    const getReportFromUrl = async () => {
      const report = await apiService.findReport(parseInt(reportId));
      const event = await apiService.getEvent(report.classId);
      const endHour = getEndHour(report.startHour, report.duration);
      const reportURL: ReportInfo = {
        id: report.id,
        email: event.teacher.email,
        fullName: event.teacher.fullName,
        date: report.date,
        startTimeClass: report.startHour,
        endTimeClass: endHour,
        studentName: event.student.fullName,
        responsibleFullName: event.responsible?.fullName,
        classLocation: report.place,
        taughtContent: report.taughtContent,
        studentPerformance: report.studentPerformance,
        generalObservation: report.observations,
        reviewRequest: report.reviewRequest,
        status: report.status,
        valueFromResponsible: report.valueFromResponsible,
        valueToTeacher: report.valueToTeacher,
        valueToTrip: report.valueToTrip,
        wasStudentAbsent: report.wasStudentAbsent,
      };
      setReportFromUrl(reportURL);
      setViewReport(true);
    };

    getReportFromUrl();
  }, []);

  const handleGenerateReportsExcel = async () => {
    const startDate = reportExcelForm.getValues('startDate').toDate();
    const endDate = reportExcelForm.getValues('endDate').toDate();
    const includeSimulatedReports = reportExcelForm.getValues(
      'includeSimulatedReports'
    );

    const excelBuffer = await apiService.getReportsExcelByDate(
      startDate,
      endDate,
      includeSimulatedReports
    );
    const dataExcel = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const fileName = `Relatórios_${dayjs(startDate).format(
      'DD-MM-YYYY'
    )}_${dayjs(endDate).format('DD-MM-YYYY')}.xlsx`;
    saveAs(dataExcel, fileName);
  };

  const generateGeneralReportsExcelButton = (
    <Button
      startIcon={<SummarizeIcon />}
      disabled={!isAdminOrEmployee}
      circular
      onClick={() => setShowGenerateReportExcelDialog(true)}
      className="w-min whitespace-nowrap"
    >
      Resumo geral
    </Button>
  );

  return (
    <PageWrapper>
      <div className="h-full">
        <div className="w-full gap-6 flex flex-col xl:flex-row justify-between">
          <div className="xl:hidden self-end flex flex-wrap gap-2">
            {showReportsSummary && (
              <Button
                circular
                startIcon={<ListAlt />}
                onClick={handleSummaryOpen}
                className="w-min whitespace-nowrap"
              >
                Resumo
              </Button>
            )}
            {showSummaryExcelButton && (
              <GenerateExcelButton
                user={selectedUser}
                filterMonth={filterMonth}
              />
            )}
            {generateGeneralReportsExcelButton}
          </div>
          <CategoryTable
            customSort={{
              Status: (a: Reports, b: Reports) => {
                if (a.Status.props.status === b.Status.props.status) return 0;
                return a.Status.props.status > b.Status.props.status ? 1 : -1;
              },
            }}
            getCategory={(row) => row.Status.props.status.toString()}
            rows={rows}
            getSummary={getReportSummary}
            isSelectable
            selectableAccordionTitle="Ver Relatório"
            newCategory={newCategory}
            isFetching={isFetchingTable}
            handleSelect={handleSelect}
            aboveHeader={(FilterInput) => (
              <div
                className={clsx('flex flex-col lg:flex-row', 'w-full gap-4')}
              >
                {user?.role !== UserRole.TEACHER && (
                  <div className="w-full">
                    <Autocomplete
                      size="small"
                      noOptionsText={'Sem opções'}
                      value={selectedUser}
                      onChange={(
                        event: object,
                        newValue: DataAutocomplete | null
                      ) => {
                        setSelectedUser(newValue);
                      }}
                      getOptionLabel={(option) => option.displayName}
                      isOptionEqualToValue={(option, value) =>
                        option.displayName === value.displayName
                      }
                      loadingText="Carregando..."
                      loading={isFetchingUser}
                      options={users}
                      renderInput={(params) => (
                        <TextField {...params} label="Selecione um Usuário" />
                      )}
                    />
                    <div className="hidden xl:inline-flex mt-2">
                      {generateGeneralReportsExcelButton}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap lg:flex-nowrap justify-between lg:justify-end gap-4">
                  <div className="w-full max-w-[200px]">
                    <ThemeProvider theme={dateTheme}>
                      <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        adapterLocale="pt-br"
                      >
                        <DatePicker
                          value={filterMonth}
                          onChange={(date) => {
                            if (!date) return;
                            setFilterMonth(date);
                          }}
                          label="Selecione um mês"
                          views={['month', 'year']}
                          formatDensity="spacious"
                          slotProps={{
                            textField: {
                              error:
                                !filterMonth ||
                                isNaN(Number(filterMonth.get('date'))),
                              size: 'small',
                              helperText: !filterMonth
                                ? 'Data é obrigatória'
                                : isNaN(Number(filterMonth.get('date')))
                                ? 'Data Inválida'
                                : '',
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </ThemeProvider>
                    <ConfirmationModal
                      onAccept={handleGenerateReportsExcel}
                      onDecline={() => setShowGenerateReportExcelDialog(false)}
                      acceptText="Gerar"
                      declineText="Cancelar"
                      open={showGenerateReportExcelDialog}
                    >
                      <div className="m-8 space-y-8">
                        <p>Selecione um intervalo:</p>
                        <DateRangePicker
                          startName="startDate"
                          endName="endDate"
                          formContext={reportExcelForm}
                        />
                        <FormProvider {...reportExcelForm}>
                          <Checkbox
                            name="includeSimulatedReports"
                            label="Simular valores de aulas sem relatório"
                          />
                        </FormProvider>
                      </div>
                    </ConfirmationModal>
                  </div>
                  {FilterInput}
                </div>
              </div>
            )}
          />
          {viewReport && (
            <TeacherReport
              title="Relatório"
              open={viewReport}
              onClose={() => {
                setViewReport(false);
                setSelectedReport(null);
              }}
              onUpdate={updateReport}
              reportInfo={reportInfo ?? reportFromUrl}
            />
          )}

          {showReportsSummary && (
            <div className="flex flex-col items-center gap-2">
              <ReportsSummary
                className="hidden xl:flex"
                selectedUserRole={selectedUser?.role as UserRole | undefined}
                reports={events.flatMap((event) => event.reports ?? [])}
                filterMonth={filterMonth}
                reportsCategory={reportsCategory}
              />

              {showSummaryExcelButton && (
                <GenerateExcelButton
                  className="hidden xl:flex"
                  user={selectedUser}
                  filterMonth={filterMonth}
                />
              )}

              <Modal
                className="xl:hidden"
                open={openSummary}
                onClose={handleSummaryClose}
                transparentBackground
              >
                <ReportsSummary
                  selectedUserRole={selectedUser?.role as UserRole | undefined}
                  reports={events.flatMap((event) => event.reports ?? [])}
                  filterMonth={filterMonth}
                  reportsCategory={reportsCategory}
                  onClose={handleSummaryClose}
                />
              </Modal>
            </div>
          )}
        </div>
      </div>
    </PageWrapper>
  );
};
